import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { fetchPrices, fetchSchedule, fetchFacilities, fetchMostRecentDate, fetchLastUpdated, MAX_DASHBOARD_DAYS, MAX_CSV_DAYS, getYesterday } from './supabase';
import { useState, useCallback, useMemo, useEffect } from 'react';
import './App.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const PRICE_FIELDS = [
  { key: 'energy',           label: 'Energy',          color: '#3a8c5c' },
  { key: 'contingency_raise',label: 'Cont. Raise',     color: '#e6a817' },
  { key: 'contingency_lower',label: 'Cont. Lower',     color: '#c8860a' },
  { key: 'regulation_raise', label: 'Reg. Raise',      color: '#4a9fd4' },
  { key: 'regulation_lower', label: 'Reg. Lower',      color: '#2a7fb8' },
  { key: 'rocof',            label: 'RoCoF',           color: '#9a6dd4' },
];

const MARKET_SERVICES = [
  'energy',
  'regulationRaise',
  'regulationLower',
  'contingencyRaise',
  'contingencyLower',
  'rocof',
];

const SERVICE_COLORS = {
  energy:           '#C8860A',
  regulationRaise:  '#1A5FAA',
  regulationLower:  '#3A7FCA',
  contingencyRaise: '#1E6B4A',
  contingencyLower: '#2D8A6A',
  rocof:            '#7B4EA0',
};

const PAGE_SIZE = 100;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(v, decimals = 2) {
  if (v === null || v === undefined || v === '') return '—';
  return Number(v).toFixed(decimals);
}

function fmtPrice(v) {
  if (v === null || v === undefined || v === '') return '—';
  return `$${Number(v).toFixed(2)}`;
}

function fmtInterval(ts) {
  if (!ts) return '—';
  return ts.replace('T', ' ').slice(0, 16);
}

function toCSV(rows) {
  if (!rows.length) return '';
  const headers = Object.keys(rows[0]);
  const escape = v => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
}

function pivotFacilityRows(rows) {
  // Group by dispatch_interval, pivot market_service into columns
  const map = {};
  for (const row of rows) {
    const key = row.dispatch_interval;
    if (!map[key]) {
      map[key] = {
        dispatch_interval: row.dispatch_interval,
        trading_day: row.trading_day,
        energy: null, regulationRaise: null, regulationLower: null,
        contingencyRaise: null, contingencyLower: null, rocof: null,
      };
    }
    map[key][row.market_service] = row.quantity;
  }
  return Object.values(map).sort((a, b) =>
    a.dispatch_interval.localeCompare(b.dispatch_interval));
}

function downloadCSV(rows, filename) {
  const blob = new Blob([toCSV(rows)], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

async function fetchAndDownloadCSV(type, startDate, endDate, facilityFilter, serviceFilter) {
  const days = (new Date(endDate) - new Date(startDate)) / 86400000;
  if (days > MAX_CSV_DAYS) {
    alert(`CSV download is limited to ${MAX_CSV_DAYS} days (~3 months).`);
    return;
  }
  try {
    let rows;
    if (type === 'prices') {
      rows = await fetchPrices(startDate, endDate);
    } else {
      if (!serviceFilter) {
        alert('Please select a market service before downloading schedule data.');
        return;
      }
      rows = await fetchSchedule(startDate, endDate, facilityFilter, serviceFilter);
    }
    downloadCSV(rows, `wem_${type}_${startDate}_${endDate}.csv`);
  } catch (e) {
    alert('Download failed: ' + e.message);
  }
}

function getDefaultDates() {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = d => d.toISOString().split('T')[0];
  return { start: fmt(start), end: fmt(end) };
}

// ── Custom Tooltip ─────────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <div className="tooltip-label">{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} className="tooltip-row">
          <span className="tooltip-dot" style={{ background: p.color }} />
          <span className="tooltip-name">{p.name}</span>
          <span className="tooltip-value">${Number(p.value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={`stat-value${accent ? ` accent-${accent}` : ''}`}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {
  const yesterday = getYesterday();
  const [startDate, setStartDate]   = useState(yesterday);
  const [endDate, setEndDate]       = useState(yesterday);
  const [loading, setLoading]           = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [error, setError]               = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [priceData, setPriceData]       = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [activeTab, setActiveTab]       = useState('prices');
  const [activeFields, setActiveFields] = useState(['energy']);
  const [facilityFilter, setFacilityFilter] = useState('');
  const [serviceFilter, setServiceFilter]   = useState('');
  const [scheduleViewMode, setScheduleViewMode] = useState('service'); // 'service' | 'facility'
  const [facilityList, setFacilityList]     = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [facilitySearch, setFacilitySearch] = useState('');
  const [page, setPage]                 = useState(0);
  const [fetchedRange, setFetchedRange] = useState({ start: '', end: '' });
  const [lastUpdated, setLastUpdated]   = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  // Auto-load most recent available date on mount
  useEffect(() => {
    Promise.all([
      fetchMostRecentDate(),
      fetchLastUpdated(),
    ]).then(([recentDate, lastUp]) => {
      setStartDate(recentDate);
      setEndDate(recentDate);
      setLastUpdated(lastUp);
      // Trigger initial fetch directly with the resolved date
      doFetch(recentDate, recentDate);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const handleFetch = useCallback(async () => {
    doFetch(startDate, endDate);
  }, [startDate, endDate]); // eslint-disable-line

  const doFetch = useCallback(async (start, end) => {
    if (!start || !end) return;
    const days = (new Date(end) - new Date(start)) / 86400000;
    if (days > MAX_DASHBOARD_DAYS) {
      setError(`Dashboard view is limited to ${MAX_DASHBOARD_DAYS} days. Use CSV download for longer ranges.`);
      return;
    }
    setLoading(true);
    setError(null);
    setPriceData([]);
    setScheduleData([]);
    setScheduleLoaded(false);
    setScheduleError(null);
    setActiveTab('prices');
    setPage(0);
    try {
      const [prices, facilities] = await Promise.all([
        fetchPrices(start, end),
        fetchFacilities(start, end),
      ]);
      setPriceData(prices);
      setFacilityList(facilities);
      setFetchedRange({ start, end });
      setSelectedFacility('');
      setFacilitySearch('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const handleLoadSchedule = useCallback(async ({ service, facility } = {}) => {
    const svc = service !== undefined ? service : serviceFilter;
    const fac = facility !== undefined ? facility : selectedFacility;
    if (scheduleViewMode === 'service' && !svc) return;
    if (scheduleViewMode === 'facility' && !fac) return;
    setLoadingSchedule(true);
    setScheduleLoaded(false);
    setScheduleData([]);
    setScheduleError(null);
    setPage(0);
    try {
      const schedule = await fetchSchedule(
        fetchedRange.start, fetchedRange.end,
        scheduleViewMode === 'facility' ? fac : '',
        scheduleViewMode === 'service' ? svc : '',
      );
      setScheduleData(schedule);
      setScheduleLoaded(true);
    } catch (e) {
      setScheduleError(e.message);
    } finally {
      setLoadingSchedule(false);
    }
  }, [serviceFilter, selectedFacility, scheduleViewMode, fetchedRange]);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (!priceData.length) return null;
    const vals = priceData.map(r => Number(r.energy)).filter(v => !isNaN(v));
    return {
      count:   priceData.length,
      avg:     vals.reduce((a, b) => a + b, 0) / vals.length,
      max:     Math.max(...vals),
      min:     Math.min(...vals),
      days:    [...new Set(priceData.map(r => r.trading_day))].length,
    };
  }, [priceData]);

  // ── Chart data ─────────────────────────────────────────────────────────────

  const chartData = useMemo(() => {
    const stride = Math.max(1, Math.floor(priceData.length / 500));
    return priceData
      .filter((_, i) => i % stride === 0)
      .map(r => ({
        t: fmtInterval(r.dispatch_interval),
        energy:            Number(r.energy) || 0,
        contingency_raise: Number(r.contingency_raise) || 0,
        contingency_lower: Number(r.contingency_lower) || 0,
        regulation_raise:  Number(r.regulation_raise) || 0,
        regulation_lower:  Number(r.regulation_lower) || 0,
        rocof:             Number(r.rocof) || 0,
      }));
  }, [priceData]);

  // ── Schedule filtering ─────────────────────────────────────────────────────

  const filteredSchedule = useMemo(() => {
    return scheduleData.filter(r =>
      (serviceFilter === '' || r.market_service === serviceFilter) &&
      (facilityFilter === '' || r.facility_code.toLowerCase().includes(facilityFilter.toLowerCase()))
    );
  }, [scheduleData, serviceFilter, facilityFilter]);

  const pivotedSchedule = useMemo(() => {
    if (scheduleViewMode !== 'facility') return [];
    return pivotFacilityRows(scheduleData);
  }, [scheduleData, scheduleViewMode]);

  const activeScheduleRows = scheduleViewMode === 'facility' ? pivotedSchedule : filteredSchedule;



  // ── Pagination ─────────────────────────────────────────────────────────────

  const activeRows    = activeTab === 'prices' ? priceData : activeScheduleRows;
  const totalPages    = Math.ceil(activeRows.length / PAGE_SIZE);
  const pageRows      = activeRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleField = key => {
    setActiveFields(prev =>
      prev.includes(key)
        ? prev.length > 1 ? prev.filter(k => k !== key) : prev
        : [...prev, key]
    );
  };

  const hasData = priceData.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="app">
      {/* Top bar */}
      <header className="topbar">
        <div className="topbar-left">
          <span className="logo-title">WEM <span className="logo-gold">PROSPECTOR</span></span>
          <span className="logo-divider" />
          <span className="logo-sub">Wholesale Electricity Market · Dispatch Solution Data</span>

        </div>
        <div className="topbar-right">
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {new Date(lastUpdated).toLocaleString('en-AU', {
                timeZone: 'Australia/Perth',
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })} AWST
            </span>
          )}
          <div className="date-group">
            <label>From</label>
            <input type="date" value={startDate}
              onChange={e => setStartDate(e.target.value)} max={endDate} />
          </div>
          <div className="date-group">
            <label>To</label>
            <input type="date" value={endDate}
              onChange={e => setEndDate(e.target.value)} min={startDate} />
          </div>
          <button className="btn-primary" onClick={handleFetch} disabled={loading}>
            {loading ? <span className="btn-spinner" /> : null}
            {loading ? 'Loading...' : 'Fetch Data'}
          </button>
        </div>
      </header>

      <main className="main">
        {/* Error */}
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Empty state */}
        {!hasData && !loading && !error && (
          <div className="empty-state">
            <div className="empty-icon">◈</div>
            <div className="empty-title">No data loaded</div>
            <div className="empty-sub">Select a date range above and click Fetch Data</div>
          </div>
        )}

        {/* Stats row */}
        {hasData && (
          <div className="stats-row">
            <StatCard label="Trading Days"    value={stats.days}                 sub={`${startDate} → ${endDate}`} />
            <StatCard label="Avg Energy"      value={fmtPrice(stats.avg)}        sub="$/MWh average"  accent="amber" />
            <StatCard label="Peak Energy"     value={fmtPrice(stats.max)}        sub="$/MWh maximum"  accent="red" />
            <StatCard label="Min Energy"      value={fmtPrice(stats.min)}        sub="$/MWh minimum"  accent="green" />
          </div>
        )}

        {/* Chart */}
        {hasData && (
          <div className="card">
            <div className="card-header">
              <div className="card-title">Clearing Prices Over Time</div>
              <div className="field-toggles">
                {PRICE_FIELDS.map(f => (
                  <button
                    key={f.key}
                    className={`field-toggle${activeFields.includes(f.key) ? ' active' : ''}`}
                    style={activeFields.includes(f.key) ? {
                      borderColor: f.color, color: f.color,
                      background: f.color + '15'
                    } : {}}
                    onClick={() => toggleField(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 4, right: 60, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#1e2530" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="t" tick={{ fill: '#8aaabf', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickLine={false} axisLine={false} interval="preserveStartEnd"
                    padding={{ right: 40 }} />
                  <YAxis tick={{ fill: '#8aaabf', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    tickLine={false} axisLine={false}
                    tickFormatter={v => `$${v}`} width={52} />
                  <Tooltip content={<CustomTooltip />} />
                  {PRICE_FIELDS.filter(f => activeFields.includes(f.key)).map(f => (
                    <Line key={f.key} type="monotone" dataKey={f.key} name={f.label}
                      stroke={f.color} strokeWidth={1.5} dot={false}
                      activeDot={{ r: 4, strokeWidth: 0 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Table */}
        {hasData && (
          <div className="card">
            <div className="card-header">
              <div className="tabs">
                <button className={`tab${activeTab === 'prices' ? ' active' : ''}`}
                  onClick={() => { setActiveTab('prices'); setPage(0); }}>
                  Prices
                  <span className="tab-count">{priceData.length.toLocaleString()}</span>
                </button>
                <button className={`tab${activeTab === 'schedule' ? ' active' : ''}`}
                  onClick={() => { setActiveTab('schedule'); setPage(0); }}>
                  Schedule
                  <span className="tab-count">
                    {loadingSchedule ? '...' : scheduleData.length.toLocaleString()}
                  </span>
                </button>
              </div>
              <div className="table-actions">
                {activeTab === 'schedule' && (
                  <>
                    <div className="view-mode-toggle">
                      <button
                        className={`view-mode-btn${scheduleViewMode === 'service' ? ' active' : ''}`}
                        onClick={() => { setScheduleViewMode('service'); setScheduleData([]); setScheduleLoaded(false); setPage(0); }}>
                        By Service
                      </button>
                      <button
                        className={`view-mode-btn${scheduleViewMode === 'facility' ? ' active' : ''}`}
                        onClick={() => { setScheduleViewMode('facility'); setScheduleData([]); setScheduleLoaded(false); setPage(0); }}>
                        By Facility
                      </button>
                    </div>
                    {scheduleViewMode === 'service' && (
                      <select className="filter-select" value={serviceFilter}
                        onChange={e => {
                          const svc = e.target.value;
                          setServiceFilter(svc);
                          handleLoadSchedule({ service: svc });
                        }}>
                        <option value="">— Select service —</option>
                        {MARKET_SERVICES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    )}
                    {scheduleViewMode === 'facility' && (
                      <>
                        <input className="filter-input" placeholder="Search facility..."
                          value={facilitySearch}
                          onChange={e => setFacilitySearch(e.target.value)} />
                        <select className="filter-select" value={selectedFacility}
                          onChange={e => {
                            const fac = e.target.value;
                            setSelectedFacility(fac);
                            handleLoadSchedule({ facility: fac });
                          }}>
                          <option value="">— Select facility —</option>
                          {facilityList
                            .filter(f => !facilitySearch || f.toLowerCase().includes(facilitySearch.toLowerCase()))
                            .map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </>
                    )}
                  </>
                )}
                <button className="btn-secondary" onClick={() =>
                  fetchAndDownloadCSV(activeTab, startDate, endDate, 
                    scheduleViewMode === 'facility' ? selectedFacility : facilityFilter, 
                    serviceFilter)}>
                  ↓ CSV (up to 3 months)
                </button>
              </div>
            </div>

            {activeTab === 'schedule' && !scheduleLoaded && !loadingSchedule && (
              <div className="tab-loading">
                {scheduleViewMode === 'service'
                  ? '← Select a market service to load schedule data'
                  : '← Search and select a facility to load schedule data'}
              </div>
            )}
            {loadingSchedule && activeTab === 'schedule' && (
              <div className="tab-loading">
                <div className="btn-spinner" style={{width:14,height:14,borderWidth:2}} />
                Loading schedule data...
              </div>
            )}
            {scheduleError && activeTab === 'schedule' && (
              <div className="alert alert-error" style={{margin:'12px 16px'}}>
                Schedule load failed: {scheduleError}
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  {activeTab === 'prices' ? (
                    <tr>
                      <th>Trading Day</th>
                      <th>Dispatch Interval</th>
                      <th>Energy</th>
                      <th>Cont. Raise</th>
                      <th>Cont. Lower</th>
                      <th>Reg. Raise</th>
                      <th>Reg. Lower</th>
                      <th>RoCoF</th>
                    </tr>
                  ) : scheduleViewMode === 'facility' ? (
                    <tr>
                      <th>Trading Day</th>
                      <th>Dispatch Interval</th>
                      <th>Energy (MW)</th>
                      <th>Cont. Raise (MW)</th>
                      <th>Cont. Lower (MW)</th>
                      <th>Reg. Raise (MW)</th>
                      <th>Reg. Lower (MW)</th>
                      <th>RoCoF (MW)</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Trading Day</th>
                      <th>Dispatch Interval</th>
                      <th>Facility</th>
                      <th>Market Service</th>
                      <th>Quantity (MW)</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {pageRows.map((row, i) => activeTab === 'prices' ? (
                    <tr key={i}>
                      <td className="mono muted">{row.trading_day}</td>
                      <td className="mono">{fmtInterval(row.dispatch_interval)}</td>
                      <td className="mono bold amber">{fmtPrice(row.energy)}</td>
                      <td className="mono">{fmt(row.contingency_raise)}</td>
                      <td className="mono">{fmt(row.contingency_lower)}</td>
                      <td className="mono">{fmt(row.regulation_raise)}</td>
                      <td className="mono">{fmt(row.regulation_lower)}</td>
                      <td className="mono">{fmt(row.rocof)}</td>
                    </tr>
                  ) : scheduleViewMode === 'facility' ? (
                    <tr key={i}>
                      <td className="mono muted">{row.trading_day}</td>
                      <td className="mono">{fmtInterval(row.dispatch_interval)}</td>
                      <td className={`mono bold ${Number(row.energy) > 0 ? 'green' : 'muted'}`}>{fmt(row.energy, 1)}</td>
                      <td className={`mono ${Number(row.contingencyRaise) > 0 ? 'green' : 'muted'}`}>{fmt(row.contingencyRaise, 1)}</td>
                      <td className={`mono ${Number(row.contingencyLower) > 0 ? 'green' : 'muted'}`}>{fmt(row.contingencyLower, 1)}</td>
                      <td className={`mono ${Number(row.regulationRaise) > 0 ? 'green' : 'muted'}`}>{fmt(row.regulationRaise, 1)}</td>
                      <td className={`mono ${Number(row.regulationLower) > 0 ? 'green' : 'muted'}`}>{fmt(row.regulationLower, 1)}</td>
                      <td className={`mono ${Number(row.rocof) > 0 ? 'green' : 'muted'}`}>{fmt(row.rocof, 1)}</td>
                    </tr>
                  ) : (
                    <tr key={i}>
                      <td className="mono muted">{row.trading_day}</td>
                      <td className="mono">{fmtInterval(row.dispatch_interval)}</td>
                      <td className="mono bold">{row.facility_code}</td>
                      <td>
                        <span className="badge" style={{
                          background: (SERVICE_COLORS[row.market_service] || '#888') + '15',
                          color: SERVICE_COLORS[row.market_service] || '#888',
                          borderColor: (SERVICE_COLORS[row.market_service] || '#888') + '40',
                        }}>
                          {row.market_service}
                        </span>
                      </td>
                      <td className={`mono bold ${Number(row.quantity) > 0 ? 'green' : 'muted'}`}>
                        {fmt(row.quantity, 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <span className="page-info">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, activeRows.length)} of {activeRows.length.toLocaleString()}
                </span>
                <div className="page-btns">
                  <button onClick={() => setPage(0)}          disabled={page === 0}>«</button>
                  <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹</button>
                  <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>›</button>
                  <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="footer">
        <div className="footer-row">
          <span>WEM Prospector</span>
          <span>·</span>
          <span>Data sourced from <a href="https://data.wa.aemo.com.au" target="_blank" rel="noreferrer">AEMO WA</a> Dispatch Solution Reference files</span>

        </div>
        <div className="footer-disclaimer">
          This data is provided for general information purposes only. WEM Prospector makes no warranty as to its accuracy or completeness. Users should verify all data independently before relying on it.
        </div>
      </footer>
    </div>
  );
}
